<script setup lang="ts">
import type { ArticleContentTypes, ArticleSize, ContentArticle } from '~/models/Content/ContentArticle.js'
import { useI18n } from 'vue-i18n'
import { computed, onMounted, onUnmounted, ref, useTemplateRef } from 'vue'
import { useMediaQuery } from '@vueuse/core'
import { faArrowsLeftRight } from '@fortawesome/duotone-regular-svg-icons'
import { useContentHelper } from '~/composables/useContentHelper.js'
import ScrollBox from '~/components/utils/ScrollBox.vue'
import RichTextRenderer from '~/components/utils/RichTextRenderer.vue'
import Tooltip from '~/components/ui/Tooltip.vue'
import Button from '~/components/ui/Button.vue'
import SubArticleNavigation from '~/components/article/SubArticleNavigation.vue'
import ArticleWordlist from '~/components/article/ArticleWordlist.vue'
import ArticleHeaderContent from '~/components/article/ArticleHeaderContent.vue'
import ArticleHeaderBar from '~/components/article/ArticleHeaderBar.vue'

const { open, article, mainContent, asideContent } = defineProps<{
  open: boolean
  article: ContentArticle
  subArticles: Array<ContentArticle>
  size: ArticleSize
  mainContent?: ArticleContentTypes[]
  asideContent: ArticleContentTypes[]
  routeParams: Record<string | number, string | number>
  hasSequenceSection: boolean
}>()

const { t } = useI18n()
const { isChatbot, isFormWriter, isCodeEditor } = useContentHelper()

const aside = useTemplateRef('aside')

const hasResized = ref(false)
const isMobileScreen = useMediaQuery('(max-width: 768px)')

const leftColumnHeader = computed(() => article.headerVariation?.identifier === 'left_column')
const hasInteractiveContent = computed(() => asideContent.some(item => isChatbot(item) || isFormWriter(item) || isCodeEditor(item)))
const themeBackground = computed(() => leftColumnHeader.value || hasInteractiveContent.value)

const width = ref<number | null>(open || leftColumnHeader.value ? null : 1)
let initial: { width: number, x: number } | undefined

const getMobileWidth = () => window.innerWidth - 32 // left margin in px
const setMobileColumn = () => width.value = isMobileScreen.value ? 1 : width.value
const setHasResized = () => hasResized.value = true

function resizeOnPointerDown(event: PointerEvent) {
  const el = event.target as Element | null
  el?.setPointerCapture(event.pointerId)
  initial = {
    x: event.x,
    width: aside.value?.clientWidth ?? 0,
  }
}

function resizeOnPointerMove(event: PointerEvent) {
  if (initial === undefined || !aside.value)
    return
  width.value = Math.max(1, initial.width + (initial.x - event.x))
}

function resizeOnClick(event: PointerEvent) {
  if ((initial?.width === aside.value?.clientWidth) || event.pointerType === '') {
    const expandedWidth = isMobileScreen.value ? getMobileWidth() : null
    width.value = width.value === 1 ? expandedWidth : 1
  }
  initial = undefined
}

onMounted(() => {
  window.addEventListener('resize', setMobileColumn)
  window.addEventListener('resize', setHasResized, { once: true })
  setMobileColumn()
})

onUnmounted(() => {
  window.removeEventListener('resize', setMobileColumn)
  window.removeEventListener('resize', setHasResized)
})
</script>

<template>
  <div
    class="au-article-column col-[fill] grid min-h-svh grid-cols-[minmax(2rem,1fr)_minmax(var(--col-width),var(--width))] overflow-x-clip md:grid-cols-[minmax(50%,1fr)_minmax(var(--col-width),var(--width))]"
    :class="{
      'border-t border-(--theme-20)': !leftColumnHeader,
    }"
    :style="{
      '--width': width !== null ? `${width}px` : '1fr',
    }"
  >
    <div class="au-article-aside relative grid h-max gap-x-(--col-gap) gap-y-(--col-width) pt-(--col-width) pb-[calc(var(--col-width)+2rem)]">
      <SubArticleNavigation
        v-if="article && subArticles && leftColumnHeader"
        :article="article"
        :sub-articles="subArticles"
        :route-params
        class="-mt-10!"
      />
      <Tooltip
        v-if="!leftColumnHeader || isMobileScreen || hasResized"
        :open="width === 1"
        :content="t('article.asideOpen')"
        side="left"
      >
        <Button
          class="group sticky left-full z-10 translate-x-1/2 !cursor-ew-resize touch-none bg-(--theme-50)"
          :class="leftColumnHeader ? 'top-[5rem]' : 'top-(--col-width)'"
          shape="square"
          :aria-label="t('article.changeWidth')"
          @pointerdown.capture="resizeOnPointerDown"
          @pointermove="resizeOnPointerMove"
          @click="resizeOnClick"
        >
          <font-awesome-icon :icon="faArrowsLeftRight" />
        </Button>
      </Tooltip>
      <div v-if="leftColumnHeader" class="col-[fill] grid grid-cols-subgrid gap-6">
        <ArticleHeaderContent
          :article
          class="au-prose col-[text]"
        />
        <ArticleHeaderBar :article class="col-[text]" />
      </div>
      <ArticleWordlist
        v-if="article.wordlist.length && leftColumnHeader"
        :item="article"
        :size
      />
      <div v-if="article?.body" class="au-prose col-[text]">
        <RichTextRenderer :text="article.body" />
      </div>
      <div class="contents" :class="{ '*:snap-start *:snap-always': hasSequenceSection }">
        <slot :items="mainContent ?? []" />
      </div>
    </div>

    <aside
      ref="aside"
      class="sticky top-0 flex max-h-svh flex-col text-lg"
      :class="{
        'border-l border-(--theme-20)': !leftColumnHeader,
        'bg-(--theme-10)': themeBackground,
        'bg-white': !themeBackground,
      }"
      style="container: aside / size;"
    >
      <ScrollBox
        class="au-article-aside grid gap-x-(--col-gap) gap-y-(--col-width) py-(--col-width)"
        :class="{ hidden: width === 1 }"
      >
        <slot :aside="true" :items="asideContent ?? []" />
      </ScrollBox>
    </aside>
  </div>
</template>

<style scoped>
.au-article-aside {
  grid-template-columns:
    [fill-start] minmax(0, 1fr)
    [media-start extext-start] minmax(0, var(--col-width))
    [text-start] minmax(0, 30ch)
    [midpoint]
    minmax(0, 30ch) [text-end]
    minmax(0, var(--col-width)) [media-end extext-end]
    minmax(0, 1fr) [fill-end];
}

@container aside (max-width: 65ch) {
  .au-article-aside {
    grid-template-columns:
      [fill-start] minmax(0, 1fr)
      [media-start extext-start text-start] minmax(0, 30ch)
      [midpoint]
      minmax(0, 30ch) [text-end media-end extext-end]
      minmax(0, 1fr) [fill-end];
  }
}
</style>
